import { Analytics } from '@analytics';
import LeftArrow from '@assets/images/Left.svg';
import TextIconInformationBox from '@components/Onboarding/TextIconInformationBox';
import { TextIconBoxType } from '@components/Onboarding/constants';
import MobileConfirmationDialog from '@components/TapToTip/MobileConfirmationDialog';
import { Button, Switch, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React from 'react';
import { useIntl } from 'react-intl';
import messages from './messages';

const useStyles = makeStyles({
  switch: {
    '& .MuiSwitch-thumb': {
      backgroundColor: '#fff'
    },
    '& .MuiSwitch-track': {
      backgroundColor: '#e0e3e0'
    },
    '& .Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#4dfa43'
    }
  }
});

const CommissionSettings = ({
  paymentCommission,
  handleReturn,
  handleUpdateCommissionSettings
}) => {
  const intl = useIntl();
  const classes = useStyles();

  const [commissionSettings, setCommissionSettings] = React.useState(paymentCommission);
  const [isConfirmationDialogShown, setIsConfirmationDialogShown] = React.useState(false);
  const [step, setActiveStep] = React.useState(0);

  const handlePercentageChange = text => {
    let number = +text.replace(/[^0-9]/g, '');
    if (number > 100) {
      number = 100;
    }
    setCommissionSettings({ ...commissionSettings, percentage: number });
  };

  const handleDecreaseStep = (submit = false) => {
    if (step === 0) {
      handleReturn();
    }
    setActiveStep(step - 1);
    if (!submit) {
      setCommissionSettings(paymentCommission);
    }
  };

  const handleSubmit = async (redirect = true) => {
    handleUpdateCommissionSettings(commissionSettings);
    Analytics.track('updated commission settings', {
      enabled: commissionSettings.enabled,
      percentage: commissionSettings.percentage
    });
    redirect && handleDecreaseStep(true);
  };

  const handleSwitch = () => {
    setIsConfirmationDialogShown(true);
    setCommissionSettings({ ...commissionSettings, enabled: !commissionSettings?.enabled });
  };

  const handleConfirmSwitch = () => {
    handleSubmit(false);
    setIsConfirmationDialogShown(false);
  };

  const handleCancelSwitch = () => {
    setCommissionSettings({ ...commissionSettings, enabled: !commissionSettings?.enabled });
    setIsConfirmationDialogShown(false);
  };

  const steps = [
    {
      key: 'settingsPage',
      subTitle: intl.formatMessage(messages.settingsPageSubTitle)
    },
    {
      key: 'percentage',
      subTitle: intl.formatMessage(messages.percentageSubTitle)
    }
  ];

  const buttons = [
    <Button
      key="confirm-button"
      className="!bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !rounded-xl !shadow-sm !normal-case"
      color="primary"
      variant="contained"
      onClick={() => {
        handleConfirmSwitch();
      }}>
      {intl.formatMessage(messages.confirmAndSave)}
    </Button>,
    <Button
      key="cancel-button"
      className="!text-slate-500 !font-semibold !normal-case"
      color="secondary"
      onClick={() => handleCancelSwitch()}>
      {intl.formatMessage(messages.cancel)}
    </Button>
  ];

  let renderBody = () => {
    switch (steps[step]?.key) {
      case 'settingsPage':
        return (
          <>
            <Button
              className="!w-[50px] !min-w-0 !p-3 !rounded-xl"
              onClick={() => handleDecreaseStep()}>
              <img src={LeftArrow} alt="" className="w-5" />
            </Button>
            <div className="mb-4">
              <div className="text-base font-semibold text-slate-800 mb-2.5">
                {intl.formatMessage(messages.commission)}
              </div>
              <div className="text-xs text-slate-500">{steps[step].subTitle}</div>
            </div>
            <div className="flex flex-col px-5 pb-5 bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 relative h-auto w-full mb-5">
              <div className="flex flex-row justify-end items-center mb-auto text-slate-500">
                <div>{intl.formatMessage(messages.active)}</div>
                <Switch
                  className={classes.switch}
                  onChange={handleSwitch}
                  checked={commissionSettings?.enabled}
                />
              </div>
              <TextIconInformationBox
                card={TextIconBoxType.CommissionPaymentLink}
                noBackgroundCircle={true}
              />
              <div className="w-full min-h-[102px] px-5 pt-5 pb-3 rounded-3xl flex flex-col bg-white my-5 justify-center items-center shadow-sm border border-solid border-slate-200">
                <div className="text-[17px] font-semibold text-slate-800 mb-2.5">
                  {intl.formatMessage(messages.commissionRate, {
                    percentage: commissionSettings?.percentage
                  })}
                </div>
              </div>
              <Button
                key="edit-commission"
                className="!bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !px-4 !py-1.5 !rounded-xl !shadow-sm !normal-case"
                onClick={() => setActiveStep(1)}>
                {intl.formatMessage(messages.edit)}
              </Button>
            </div>
          </>
        );
      case 'percentage':
        return (
          <>
            <Button
              className="!w-[50px] !min-w-0 !p-3 !rounded-xl"
              onClick={() => handleDecreaseStep()}>
              <img src={LeftArrow} alt="" className="w-5" />
            </Button>
            <div className="mb-4">
              <div className="text-base font-semibold text-slate-800 mb-2.5">
                {intl.formatMessage(messages.commission)}
              </div>
              <div className="text-xs text-slate-500">{steps[step].subTitle}</div>
            </div>

            <div className="flex flex-col px-5 pb-5 bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 relative h-auto w-full mb-5">
              <TextField
                label={intl.formatMessage(messages.percentageAmount)}
                placeholder="0"
                type="number"
                value={commissionSettings?.percentage.toString()}
                className="!my-5 bg-white"
                onChange={e => handlePercentageChange(e.target.value)}
                maxLength={10}
                variant="outlined"
                required
              />
              <Button
                key="edit-commission"
                color="primary"
                variant="contained"
                className="!bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !py-2.5 !rounded-xl !shadow-sm !normal-case"
                onClick={() => handleSubmit()}>
                {intl.formatMessage(messages.saveAndApply)}
              </Button>
            </div>
          </>
        );
      default:
        break;
    }
  };

  return (
    <>
      <div className="flex flex-col">{renderBody()}</div>
      <MobileConfirmationDialog
        isShown={isConfirmationDialogShown}
        buttons={buttons}
        close={handleCancelSwitch}
        textIconBoxType={
          commissionSettings?.enabled
            ? TextIconBoxType.CommissionWarningEnable
            : TextIconBoxType.CommissionWarningDisable
        }
      />
    </>
  );
};

export default CommissionSettings;
